import { initSentry } from "@/lib/sentry"

initSentry()

// Auto-reload once when a stale chunk fails to load after a new deploy.
// Vite fires this event when a dynamic import()ed chunk 404s.
window.addEventListener("vite:preloadError", () => {
	const last = Number(sessionStorage.getItem("chunk_reload") || 0)
	if (Date.now() - last > 10_000) {
		sessionStorage.setItem("chunk_reload", String(Date.now()))
		window.location.reload()
	}
})

import { Buffer } from "buffer"

window.Buffer = Buffer

import { PrivyProvider } from "@privy-io/react-auth"
import * as Sentry from "@sentry/react"
import { createSolanaRpc, createSolanaRpcSubscriptions } from "@solana/kit"
import { ConvexProviderWithAuth, ConvexReactClient } from "convex/react"
import posthog from "posthog-js"
import { PostHogProvider } from "posthog-js/react"
import { StrictMode } from "react"
import { createRoot } from "react-dom/client"
import { BrowserRouter } from "react-router-dom"
import { Toaster } from "sonner"
import App from "@/App"
import "@/index.css"
import {
	CONVEX_URL,
	POSTHOG_API_KEY,
	POSTHOG_HOST,
	PRIVY_APP_ID,
	PRIVY_CLIENT_ID,
	SOLANA_RPC_URL,
} from "@/lib/env"
import { usePrivyConvexAuth } from "@/lib/usePrivyConvexAuth"

if (POSTHOG_API_KEY) {
	posthog.init(POSTHOG_API_KEY, {
		api_host: POSTHOG_HOST,
		capture_pageview: false,
		persistence: "localStorage",
		person_profiles: "identified_only",
	})
}

const convex = new ConvexReactClient(CONVEX_URL)

const solanaRpcConfig = SOLANA_RPC_URL
	? {
			rpcs: {
				"solana:mainnet": {
					rpc: createSolanaRpc(SOLANA_RPC_URL),
					rpcSubscriptions: createSolanaRpcSubscriptions(
						SOLANA_RPC_URL.replace("https://", "wss://"),
					),
				},
			},
		}
	: undefined

const toasterProps = {
	position: "bottom-center" as const,
	theme: "dark" as const,
	toastOptions: {
		style: { background: "#1a1a1a", border: "1px solid #2a2a2a", color: "#e5e5e5" },
	},
}

function Shell({ children }: { children: React.ReactNode }) {
	return (
		<BrowserRouter>
			<Sentry.ErrorBoundary
				fallback={<p className="p-8 text-center text-red-400">Something went wrong.</p>}
			>
				{children}
			</Sentry.ErrorBoundary>
			<Toaster {...toasterProps} />
		</BrowserRouter>
	)
}

function Root() {
	return (
		<PostHogProvider client={posthog}>
			<PrivyProvider
				appId={PRIVY_APP_ID}
				clientId={PRIVY_CLIENT_ID}
				config={{
					appearance: {
						theme: "dark",
						accentColor: "#39FF14",
						walletChainType: "solana-only",
					},
					embeddedWallets: {
						// Match mobile: whitelabel OAuth routes through explicit wallet
						// setup before we bootstrap the Convex user record.
						ethereum: { createOnLogin: "off" },
						solana: { createOnLogin: "off" },
						showWalletUIs: false,
					},
					loginMethods: ["twitter", "email"],
					solana: solanaRpcConfig,
				}}
			>
				<ConvexProviderWithAuth client={convex} useAuth={usePrivyConvexAuth}>
					<Shell>
						<App />
					</Shell>
				</ConvexProviderWithAuth>
			</PrivyProvider>
		</PostHogProvider>
	)
}

// biome-ignore lint/style/noNonNullAssertion: root element always exists in index.html
createRoot(document.getElementById("root")!).render(
	<StrictMode>
		<Root />
	</StrictMode>,
)
